'use strict';

Object.defineProperty(exports, "__esModule", {
    value: true
});

var _toConsumableArray2 = require('babel-runtime/helpers/toConsumableArray');

var _toConsumableArray3 = _interopRequireDefault(_toConsumableArray2);

var _getIterator2 = require('babel-runtime/core-js/get-iterator');

var _getIterator3 = _interopRequireDefault(_getIterator2);

exports.targetEnhance = targetEnhance;

var _fireEvent = require('@nimius/event-utility/src/fire-event');

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * @typedef {Object} targetEnhance~options
 *
 * @property {string} [attribute=data-target-enhancement] - The attribute that is used to initialize the target
 *           enhancement on elements.
 *
 * @property {string[]} [targetAttributes=['data-target-enhancement-target', 'href']] - Which attributes to probe for
 *           selectors to the target. By default the attribute `data-target-enhancement-target` is used with a fallback
 *           to `href`. Note, that not using `href` means that you will loose functionality for users without Javascript.
 *
 * @property {string} [targetClassAttribute=data-target-enhancement-class] - Attribute that is used to specify the
 *           class, that the target should receive.
 *
 * @property {string} [scrollAttribute=data-target-enhancement-smooth-scroll] - Attribute that indicates, whether smooth
 *           scrolling should be active or not.
 *
 * @property {number} [smoothScrollSpeed=500] - The default smooth scrolling speed in milliseconds.
 *
 * @property {string} [exclusiveGroupAttribute=data-target-enhancement-exclusive-group] - Attribute that specifies the
 *           exclusive group.
 */

/**
 * @description Enables target enhancement for the given element.
 * @summary
 * This method is useful, if your aim is to have fine-grained control over which elements receive
 * target enhancements and which do not.
 *
 * If you want to enable target enhancement globally, then {@link initializeGlobalTargetEnhancement} might
 * be of more use to you.
 *
 * @example <caption>Initializes target enhancement for this specific element</caption>
 * // <a id="toggle" href="#element" data-target-enhancement">show</a>
 * // <div id="element">content</div>
 *
 * import { targetEnhance } from '@nimius/target-enhancement';
 * targetEnhance(document.querySelector('#toggle'));
 *
 * @param {HTMLElement} element - The element to enhance.
 * @param {targetEnhance~options} [options] - The options used to enhance the element.
 */
function targetEnhance(element) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    options.targetAttributes = options.targetAttributes || ['data-target-enhancement-target', 'href'];
    options.targetClassAttribute = options.targetClassAttribute || 'data-target-enhancement-class';
    options.scrollAttribute = options.scrollAttribute || 'data-target-enhancement-smooth-scroll';
    options.exclusiveGroupAttribute = options.exclusiveGroupAttribute || 'data-target-enhancement-exclusive-group';
    options.smoothScrollSpeed = options.smoothScrollSpeed || 500;

    var classes = (element.getAttribute(options.targetClassAttribute) || 'is-target').replace(/[^\w-_@:]/, ' ').replace(/\s+/, ' ').split(/\s/).map(function (clss) {
        return clss.trim();
    });
    var activateSmoothScroll = element.hasAttribute(options.scrollAttribute);
    var activateExclusiveGroup = element.hasAttribute(options.exclusiveGroupAttribute);

    // Search for the target
    var targets = [];
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
        for (var _iterator = (0, _getIterator3.default)(options.targetAttributes), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
            var targetAttribute = _step.value;

            var selector = element.getAttribute(targetAttribute);
            if (!selector) {
                continue;
            }

            // The document.querySelector is wrapped in an try/catch block in order to
            // not error-out on invalid selectors such as `#0`
            try {
                targets = [].concat((0, _toConsumableArray3.default)(document.querySelectorAll(selector)));
            } catch (e) {
                console.warn(e);
            }

            if (targets.length > 0) {
                break;
            }
        }

        // If there is no target, then there is no point in doing anything else
    } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
    } finally {
        try {
            if (!_iteratorNormalCompletion && _iterator.return) {
                _iterator.return();
            }
        } finally {
            if (_didIteratorError) {
                throw _iteratorError;
            }
        }
    }

    if (targets.length === 0) {
        console.warn('Target enhancement: No targets found');
        return;
    }

    /**
     * Opens the current elements targets.
     * @private
     */
    var open = function open() {
        // Close all members of an exclusive group before doing more.
        if (activateExclusiveGroup) {
            var exclusiveGroup = element.getAttribute(options.exclusiveGroupAttribute);
            var members = document.querySelectorAll('[' + options.exclusiveGroupAttribute + '="' + exclusiveGroup + '"]');
            var _iteratorNormalCompletion2 = true;
            var _didIteratorError2 = false;
            var _iteratorError2 = undefined;

            try {
                for (var _iterator2 = (0, _getIterator3.default)(members), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                    var exclusiveGroupMember = _step2.value;

                    (0, _fireEvent.fireEvent)(exclusiveGroupMember, 'target-enhancement.close');
                }
            } catch (err) {
                _didIteratorError2 = true;
                _iteratorError2 = err;
            } finally {
                try {
                    if (!_iteratorNormalCompletion2 && _iterator2.return) {
                        _iterator2.return();
                    }
                } finally {
                    if (_didIteratorError2) {
                        throw _iteratorError2;
                    }
                }
            }
        }

        // Toggle the class

        var _loop = function _loop(target) {
            classes.forEach(function (clss) {
                return target.classList.add(clss);
            });
        };

        var _iteratorNormalCompletion3 = true;
        var _didIteratorError3 = false;
        var _iteratorError3 = undefined;

        try {
            for (var _iterator3 = (0, _getIterator3.default)(targets), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
                var target = _step3.value;

                _loop(target);
            }

            // Scroll to the element, if activated.
        } catch (err) {
            _didIteratorError3 = true;
            _iteratorError3 = err;
        } finally {
            try {
                if (!_iteratorNormalCompletion3 && _iterator3.return) {
                    _iterator3.return();
                }
            } finally {
                if (_didIteratorError3) {
                    throw _iteratorError3;
                }
            }
        }

        if (activateSmoothScroll) {
            // TODO implement smooth scrolling for all browsers
            // const smoothScrollSpeed = parseInt(element.getAttribute(options.scrollAttribute)) || options.smoothScrollSpeed;
            targets[0].scrollIntoView({ behavior: 'smooth' });
        }
    };

    /**
     * Closes the current elements targets.
     * @private
     */
    var close = function close() {
        var _loop2 = function _loop2(target) {
            classes.forEach(function (clss) {
                return target.classList.remove(clss);
            });
        };

        var _iteratorNormalCompletion4 = true;
        var _didIteratorError4 = false;
        var _iteratorError4 = undefined;

        try {
            for (var _iterator4 = (0, _getIterator3.default)(targets), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
                var target = _step4.value;

                _loop2(target);
            }
        } catch (err) {
            _didIteratorError4 = true;
            _iteratorError4 = err;
        } finally {
            try {
                if (!_iteratorNormalCompletion4 && _iterator4.return) {
                    _iterator4.return();
                }
            } finally {
                if (_didIteratorError4) {
                    throw _iteratorError4;
                }
            }
        }
    };

    /**
     * Toggles the current element between the open and closed state.
     * @private
     */
    var toggle = function toggle() {
        var isActive = targets[0].classList.contains(classes[0]);

        if (isActive) {
            (0, _fireEvent.fireEvent)(element, 'target-enhancement.close');
        } else {
            (0, _fireEvent.fireEvent)(element, 'target-enhancement.open');
        }
    };

    /*
     * Event Listeners.
     */
    element.addEventListener('target-enhancement.open', function () {
        return open();
    });
    element.addEventListener('target-enhancement.close', function () {
        return close();
    });
    element.addEventListener('target-enhancement.toggle', function () {
        return toggle();
    });
    element.addEventListener('click', function (e) {
        e.preventDefault();
        (0, _fireEvent.fireEvent)(element, 'target-enhancement.toggle');
    });
}


//////////////////
// WEBPACK FOOTER
// ./node_modules/@nimius/target-enhancement/src/target-enhance.js
// module id = 60
// module chunks = 4